import React, {FC, useState, useEffect} from "react"
import moment from "moment"

import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {Body, H1} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import Link from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button"
import authentication from "@irati/auth"
import workosService from "@irati/workos/services/workos"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {resendWelcomeEmails} from "@irati/auth/services/password"
import buService from "@irati/billing/services/bu"
import {ZuoraCurrentPlanWindow} from "@irati/types"
import adminTracking from "@irati-tracking/admin"

import EnabledDisabledLabel from "../../EnabledDisabledLabel"
import InvitationEmailsModal from "../SetupInformation/InvitationEmailsModal"
import Testimonials from "./Testimonials"
import Benefits from "./Benefits"
import Providers from "./Providers"
import HRISSetup from "./Setup"
import HRISUpsell from "./Upsell"
import SetupLink from "./Upsell/SetupLink"
import FirstMonthFreeOffer from "./FirstMonthFreeOffer"
import {HeaderContainer, HeaderImg} from "./styled"

interface Props extends InjectIntl {}

const HRISHomepage: FC<Props> = ({formatMessage}) => {
  const [upsell, setUpsell] = useState<boolean>(false)
  const [setup, setSetup] = useState<boolean>(false)
  const [generateLink, setGenerateLink] = useState(false)
  const [submit, setSubmit] = useState<Promise<void> | null>(null)
  const [submitLink, setSubmitLink] = useState<Promise<void> | null>(null)
  const [launchDate, setLaunchDate] = useState<string | null>(null)
  const [confirmLaunchEmails, setConfirmLaunchEmails] = useState(false)
  const [submitConfirmation, setSubmitConfirmation] = useState<Promise<void> | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [renewalPending, setRenewalPending] = useState(false)
  const [dsyncEnabled, setDsyncEnabled] = useState(false)
  const [hint, setHint] = useState(false)

  const theme = useTheme()

  const handleOnClose = () => {
    setUpsell(false)
    setSetup(false)
  }

  const handleOnSubmit = (load) => {
    setSubmitLink(load)
    setGenerateLink(false)
  }

  const getOrganisation = () => {
    workosService.getOrganisation(authentication.business().uuid).then((organisation) => {
      if (organisation?.organisationId && organisation?.directoryId) {
        getDsyncConfig()
      }
      setDsyncEnabled(organisation?.organisationId && organisation?.directoryId)
    })
  }

  const getDsyncConfig = () => {
    workosService
      .getDsyncConfig(authentication.business().uuid)
      .then(({directorySyncConfig}) => {
        if (directorySyncConfig?.launchAt?.seconds) {
          const date = moment.unix(directorySyncConfig?.launchAt.seconds)
          setLaunchDate(date.isSameOrBefore() ? date.format("DD/MMM/YYYY") : null)
        } else {
          setLaunchDate("")
        }
      })
      .catch(() => {
        setLaunchDate("")
      })
  }

  const getRenewalPending = () => {
    const businessId = authentication.business().uuid
    buService.getCurrentPlan({businessId}).then((plan) => {
      if (
        plan.window === ZuoraCurrentPlanWindow.RenewalWindow ||
        plan.window === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation
      ) {
        setRenewalPending(true)
      }
    })
  }

  const editDsyncIntegration = () => {
    const load = workosService.generatePortalLinks(authentication.business().uuid).then((portalLinks) => {
      if (portalLinks?.directorySyncUrl) {
        window.location = portalLinks.directorySyncUrl
      }
    })
    setSubmit(load)
  }

  const sendActivationEmails = () => {
    const submitted = resendWelcomeEmails(authentication.business().uuid).then(() => {
      workosService.setDsyncConfig(authentication.business().uuid, {seconds: moment().unix().toString()}).then(() => {
        setLaunchDate(moment().format("DD/MMM/YYYY"))
        setShowSuccess(true)
      })
    })
    setSubmitConfirmation(submitted)
  }

  const handleStartUpsell = () => {
    adminTracking.hrisUpsellSetup("dysnc_homepage", {source: "Navigation", viewType: "Default"})
    setUpsell(true)
  }

  useEffect(() => {
    if (!authentication.apps("admin.directorySync")) {
      getRenewalPending()
    }
    getOrganisation()
  }, [])

  const notPurchased = !authentication.apps("admin.directorySync")
  const inactivePurchased = !!authentication.apps("admin.directorySync") && !dsyncEnabled
  const activePurchased = !!authentication.apps("admin.directorySync") && dsyncEnabled

  const launchEmailsTitle =
    launchDate !== ""
      ? "admin.components.integration.directorySync.activationEmails.text2"
      : "admin.components.integration.directorySync.activationEmails.text3"

  return (
    <>
      <HeaderContainer>
        <Flex padding={[1, 3]} justifyContent="space-between">
          <Flex marginBottom={3} direction="column">
            {activePurchased && (
              <Flex marginBottom marginRight>
                {launchDate === null && (
                  <Alert
                    type={AlertTypes.warning}
                    title="admin.components.integration.directorySync.activationEmails.text1"
                    link="admin.components.integration.directorySync.activationEmails.button1"
                    onClickLink={() => setConfirmLaunchEmails(true)}
                  />
                )}
                {(launchDate || launchDate === "") && (
                  <Alert
                    type={AlertTypes.info}
                    title={launchEmailsTitle}
                    link="admin.components.integration.directorySync.activationEmails.button2"
                    values={{date: launchDate}}
                    onClickLink={() => setConfirmLaunchEmails(true)}
                  />
                )}
                <InvitationEmailsModal
                  text="admin.components.integration.directorySync.activationEmailsModal.text"
                  button="admin.components.integration.directorySync.activationEmailsModal.button"
                  show={confirmLaunchEmails}
                  showSuccess={showSuccess}
                  onHide={() => setConfirmLaunchEmails(false)}
                  submit={submitConfirmation}
                  onConfirm={sendActivationEmails}
                />
              </Flex>
            )}
            <H1 textAlign="left" maxWidth="650px" marginBottom={3}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.title" />
              <EnabledDisabledLabel enabled={dsyncEnabled} purchased={!!authentication.apps("admin.directorySync")} />
            </H1>

            {notPurchased && <FirstMonthFreeOffer />}

            <Body color={theme.colors.greys.black}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.subTitle" />
            </Body>
            {(notPurchased || inactivePurchased) && (
              <Body color={theme.colors.greys.black} marginBottom={2}>
                <FormattedMessage
                  id="admin.components.integration.directorySync.hris.subTitle2"
                  values={{
                    link: (
                      <Link
                        cta
                        href={formatMessage({id: "admin.components.integration.directorySync.hris.providers.link"})}
                        target="_blank">
                        <FormattedMessage id="admin.components.integration.directorySync.hris.link" />
                      </Link>
                    )
                  }}
                />
              </Body>
            )}
            {activePurchased && (
              <Body color={theme.colors.greys.black} marginBottom={2}>
                <FormattedMessage id="admin.components.integration.directorySync.hris.subTitle3" />
              </Body>
            )}
            <Flex direction="column" maxWidth="300px">
              {notPurchased && !renewalPending && (
                <Button primary large onClick={handleStartUpsell} marginBottom>
                  <FormattedMessage id="admin.components.integration.directorySync.hris.button" />
                </Button>
              )}
              {notPurchased && renewalPending && (
                <Button primary large onClick={() => setHint(true)} marginBottom>
                  <FormattedMessage id="admin.components.integration.directorySync.hris.button6" />
                </Button>
              )}
              {inactivePurchased && (
                <Button primary large onClick={() => setSetup(true)} marginBottom>
                  <FormattedMessage id="admin.components.integration.directorySync.hris.button3" />
                </Button>
              )}
              {(notPurchased || inactivePurchased) && (
                <Body small color={theme.colors.greys.mediumDark}>
                  {!hint && <FormattedMessage id="admin.components.integration.directorySync.hris.subText" />}
                  {hint && <FormattedMessage id="admin.components.integration.directorySync.hris.subText2" />}
                </Body>
              )}
            </Flex>
            {activePurchased && (
              <>
                <Flex wrap="wrap" direction="column" marginBottom marginRight maxWidth="600px">
                  <SetupLink
                    show={generateLink}
                    title="admin.components.integration.directorySync.hris.upsell.generateSetupLink.generatedLink2"
                    onSubmit={handleOnSubmit}
                  />
                </Flex>
                <Flex wrap="wrap" gap={2}>
                  <Button primary large load={submitLink} onClick={() => setGenerateLink(true)} marginBottom>
                    <FormattedMessage id="admin.components.integration.directorySync.hris.button4" />
                  </Button>
                  <Button primary large load={submit} onClick={editDsyncIntegration} marginBottom>
                    <FormattedMessage id="admin.components.integration.directorySync.hris.button5" />
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
          <HeaderImg src={formatMessage({id: "admin.components.integration.directorySync.hris.img"})} />
        </Flex>
      </HeaderContainer>

      <Benefits />
      <Providers />
      <Testimonials />
      <HRISSetup />
      <HRISUpsell showUpsell={upsell} finishSetup={setup} onClose={handleOnClose} />

      <Flex justifyContent="center" marginBottom={3}>
        {notPurchased && !renewalPending && (
          <Button primary small onClick={() => setUpsell(true)} marginBottom>
            <FormattedMessage id="admin.components.integration.directorySync.hris.button" />
          </Button>
        )}
        {inactivePurchased && (
          <Button primary small onClick={() => setSetup(true)} marginBottom>
            <FormattedMessage id="admin.components.integration.directorySync.hris.button3" />
          </Button>
        )}
      </Flex>
      <Flex height="650px" width="100%">
        <iframe
          title="integrationsVideo"
          src={formatMessage({id: "admin.containers.integrations.video"})}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          mozallowfullscreen
          webkitallowfullscreen
          width="100%"
          height="100%"
        />
      </Flex>
    </>
  )
}

export default injectIntl(HRISHomepage)
